
const DEFAULT_CONFIG = {
  minScale: 0.5,
  maxScale: 5,
  step: 0.1,
  onChange: undefined
};

const createDefaultState = () => ({
  scale: 1,
  translateX: 0,
  translateY: 0,
  canZoomIn: true,
  canZoomOut: true,
  isDefault: true
});

export const initializeZoom = (svgContainer, svg, options = {}) => {
  if (!svgContainer || !svg) {
    const defaultState = createDefaultState();
    const noop = () => defaultState;
    return {
      zoomIn: noop,
      zoomOut: noop,
      resetTransform: noop,
      getState: () => defaultState,
      destroy: () => {}
    };
  }

  const config = { ...DEFAULT_CONFIG, ...options };
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let isPanning = false;
  let startX = 0;
  let startY = 0;
  let pendingTranslateX = 0;
  let pendingTranslateY = 0;
  let panFrameId = null;

  svg.style.transformOrigin = '0 0';
  svg.style.willChange = 'transform';
  svg.style.cursor = 'grab';

  const previousInlineTransition = svg.style.transition;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getState = () => ({
    scale,
    translateX,
    translateY,
    canZoomIn: scale < config.maxScale,
    canZoomOut: scale > config.minScale,
    isDefault: scale === 1 && translateX === 0 && translateY === 0
  });

  const emitState = () => {
    if (typeof config.onChange === 'function') {
      config.onChange(getState());
    }
  };

  const updateTransform = () => {
    svg.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
    emitState();
  };

  const zoomAtPoint = (nextScale, offsetX, offsetY) => {
    const clampedScale = clamp(nextScale, config.minScale, config.maxScale);

    if (clampedScale === scale) {
      emitState();
      return;
    }

    const scaleFactor = clampedScale / scale;
    translateX = offsetX - scaleFactor * (offsetX - translateX);
    translateY = offsetY - scaleFactor * (offsetY - translateY);
    scale = clampedScale;
    updateTransform();
  };

  const zoomByStep = (direction) => {
    const rect = svgContainer.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    zoomAtPoint(scale + direction * config.step, centerX, centerY);
    return getState();
  };

  const zoomIn = () => zoomByStep(1);

  const zoomOut = () => zoomByStep(-1);

  const resetTransform = () => {
    scale = 1;
    translateX = 0;
    translateY = 0;
    updateTransform();
    return getState();
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const rect = svgContainer.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const direction = event.deltaY < 0 ? 1 : -1;
    zoomAtPoint(scale + direction * config.step, offsetX, offsetY);
  };

  const handleMouseDown = (event) => {
    if (event.button !== 0) {
      return;
    }

    if (event.target.closest('[data-sld-zoom-control="true"]')) {
      return;
    }

    event.preventDefault();
    isPanning = true;
    startX = event.clientX - translateX;
    startY = event.clientY - translateY;
    pendingTranslateX = translateX;
    pendingTranslateY = translateY;
    svg.style.transition = 'none';
    svg.style.cursor = 'grabbing';
  };

  const handleMouseUp = () => {
    if (!isPanning) {
      return;
    }

    isPanning = false;

    if (panFrameId !== null) {
      cancelAnimationFrame(panFrameId);
      panFrameId = null;
      translateX = pendingTranslateX;
      translateY = pendingTranslateY;
      updateTransform();
    }

    svg.style.transition = previousInlineTransition;
    svg.style.cursor = 'grab';
  };

  const handleMouseMove = (event) => {
    if (!isPanning) {
      return;
    }

    event.preventDefault();
    pendingTranslateX = event.clientX - startX;
    pendingTranslateY = event.clientY - startY;

    if (panFrameId !== null) {
      return;
    }

    panFrameId = requestAnimationFrame(() => {
      panFrameId = null;
      translateX = pendingTranslateX;
      translateY = pendingTranslateY;
      updateTransform();
    });
  };

  svgContainer.addEventListener('wheel', handleWheel, { passive: false });
  svgContainer.addEventListener('mousedown', handleMouseDown);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('mousemove', handleMouseMove);

  emitState();

  return {
    zoomIn,
    zoomOut,
    resetTransform,
    getState,
    destroy: () => {
      svgContainer.removeEventListener('wheel', handleWheel);
      svgContainer.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);

      if (panFrameId !== null) {
        cancelAnimationFrame(panFrameId);
      }

      svg.style.transition = previousInlineTransition;
      svg.style.willChange = '';
      svg.style.cursor = '';
    }
  };
};
